import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowLeftRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';
import { ShiftSwapRequestView } from '../../../../models/shiftSwap';
import { formatShiftSummary, formatLocalShiftSummary } from './format';

interface ApproveSwapDialogProps {
  swap: ShiftSwapRequestView | null;
  submitting: boolean;
  locale?: string;
  onClose: () => void;
  onConfirm: (comments: string) => void;
}

// Prefer the timezone-safe local wall-clock; fall back to the UTC field only when
// the server did not supply a local one. Mirrors AcceptSwapDialog's helper.
const shiftSummary = (
  localStart: string | null | undefined,
  localEnd: string | null | undefined,
  utcStart: string | null | undefined,
  utcEnd: string | null | undefined,
  locale?: string,
): string => {
  if (localStart && localEnd) return formatLocalShiftSummary(localStart, localEnd, locale);
  if (utcStart && utcEnd) return formatShiftSummary(utcStart, utcEnd, locale);
  return '';
};

// Mirrors RejectSwapDialog, but the comment is optional and the action is
// affirmative. The backend + mobile already carry the approver's note; the web
// approve flow used to drop it silently.
const ApproveSwapDialog: React.FC<ApproveSwapDialogProps> = ({ swap, submitting, locale, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState('');

  useEffect(() => {
    if (swap) setComments('');
  }, [swap]);

  const open = swap !== null;
  const isOneWay = !!swap && (!swap.counterpartyShiftStartTime || !swap.counterpartyShiftEndTime);

  const requesterSummary = swap
    ? shiftSummary(swap.requesterShiftLocalStart, swap.requesterShiftLocalEnd, swap.requesterShiftStartTime, swap.requesterShiftEndTime, locale)
    : '';
  const counterpartySummary = swap
    ? shiftSummary(swap.counterpartyShiftLocalStart, swap.counterpartyShiftLocalEnd, swap.counterpartyShiftStartTime, swap.counterpartyShiftEndTime, locale)
    : '';

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !submitting) onClose(); }}>
      <DialogContent
        size="sm"
        onEscapeKeyDown={(e) => { if (submitting) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (submitting) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{t('Approve this swap')}</DialogTitle>
          <DialogDescription>
            {t('Both employees will be reassigned to their new shifts.')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {swap && (
            <div className="space-y-1.5 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <ArrowRight size={14} className="shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">{swap.requesterName}</span>
                <span className="font-medium text-foreground tabular-nums">{requesterSummary}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <ArrowLeftRight size={14} className="shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">{swap.counterpartyName}</span>
                <span className="font-medium text-foreground tabular-nums">
                  {isOneWay ? t('Nothing (one-way cover)') : counterpartySummary}
                </span>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="approve-comment" className="mb-2 block text-sm font-medium text-foreground">
              {t('Note')} <span className="font-normal text-muted-foreground">{t('(optional)')}</span>
            </label>
            <textarea
              id="approve-comment"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              disabled={submitting}
              placeholder={t('Add an optional note for the employees')}
              data-testid="swap-approve-comment"
              className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" size="sm" disabled={submitting} onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="success"
            size="sm"
            loading={submitting}
            onClick={() => onConfirm(comments.trim())}
            data-testid="swap-approve-confirm"
          >
            {t('Approve swap')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApproveSwapDialog;
